import Vue from 'vue'
import Vuex from 'vuex'
import axios from '@/axios';
import auth from './modules/auth'
import favorites from './modules/favorites'
import appointments from './modules/appointments'
import comparison from './modules/comparison'
import billing from './modules/billing'
import { buildFilters } from '@/utils/filters'
import { hexToHSL, ThemeManager } from '@/utils/colors'
import { updateFavicon } from '@/utils/favicon'
import rent from './modules/rent'
import savedSearches from './modules/savedSearches'

Vue.use(Vuex)

export default new Vuex.Store({
  state: {
    inmob_data: {},
    accessControl: {
      subscriptionBlocked: false,
      reason: null,
      inmobiliaria: null,
    },
    locations: [],
    publications: {
      count: 0,
      next: null,
      previous: null,
      results: [],
    },
    home: {
      recommended: [],
      latest: [],
    },
    blog: {
      posts: [],
    },
    search: {
      filters: {
        mts_min: null,
        mts_max: null,
        property_type: "casa",
        transaction_type: "venta",
        bedrooms: null,
        baths: null,
        locations: null,
      },
    },
  },
  getters: {
    filteredProperties: (state) => (filters) => {
      return state.publications.results.filter(property => {
        return (!filters.mts_max || property.metros_cuadrados <= filters.mts_max) &&
               (!filters.mts_min || property.metros_cuadrados >= filters.mts_min) &&
               (!filters.property_type || property.property_type === filters.property_type) &&
               (!filters.transaction_type || property.transaction_type === filters.transaction_type) &&
               (!filters.bedrooms || property.bedrooms >= filters.bedrooms) &&
               (!filters.baths || property.baths >= filters.baths) &&
               (!filters.locations || property.locations === filters.locations);
      });
    }
  },
  mutations: {
    SET_STYLING: (state, inmob_data) => {
      state.inmob_data = inmob_data
    },
    SET_SUBSCRIPTION_BLOCKED: (state, payload) => {
      state.accessControl.subscriptionBlocked = true
      state.accessControl.reason = payload?.reason || 'subscription_inactive'
      state.accessControl.inmobiliaria = payload?.inmobiliaria || null
    },
    CLEAR_SUBSCRIPTION_BLOCKED: (state) => {
      state.accessControl.subscriptionBlocked = false
      state.accessControl.reason = null
      state.accessControl.inmobiliaria = null
    },
    SET_PROPERTIES: (state, {results, count, next, previous}) => {
      state.publications.results = results;
      state.publications.count = count;
      state.publications.next = next;
      state.publications.previous = previous;
    },
    SET_LOCATIONS: (state, locations) => {
      state.locations = locations;
    },
    SET_PROPERTIES_HOME_PROPERTIES: (state, properties) => {
      state.home.recommended = properties.recommended || [];
      state.home.latest = properties.latest || [];
    },
    SET_BLOG_POSTS: (state, posts) => {
      state.blog.posts = posts;
    },
    SET_FILTERS: (state, filters) => {
      state.search.filters = filters;
    },
    UPDATE_FILTERS: (state, filters) => {
      state.search.filters = { ...state.search.filters, ...filters };
    },
  },
  actions: {
    FETCH_CUSTOMIZATIONS: async ({ commit }) => {
      try {
        commit('CLEAR_SUBSCRIPTION_BLOCKED')
        const response = await axios.get(
          `${process.env.VUE_APP_API_BASE_URL}/api/company/${window.location.host}/customization/`
        );
        const inmob_data = response.data;
        commit('SET_STYLING', inmob_data);

        if (inmob_data && inmob_data.inmobiliaria_details) {
          document.title = inmob_data.inmobiliaria_details.nombre;
          
          // Actualizar favicon con el logo de la inmobiliaria
          if (inmob_data.inmobiliaria_details.logo) {
            updateFavicon(
              inmob_data.inmobiliaria_details.logo,
              process.env.VUE_APP_API_BASE_URL
            );
          }
        }

        const themeData = inmob_data.theme;

        if (themeData.type === 'predefined') {
          document.documentElement.setAttribute('data-theme', themeData.name)
        } else if (themeData.type === 'custom' && themeData.styles) {
          const estilos = themeData.styles
          const manager = new ThemeManager(estilos)
          manager.apply()
        }

        return inmob_data;
      } catch (error) {
        const accessCode = error?.response?.data?.code
        if (error?.response?.status === 402 && ['subscription_inactive', 'trial_expired'].includes(accessCode)) {
          commit('SET_SUBSCRIPTION_BLOCKED', {
            reason: accessCode,
            inmobiliaria: error.response.data.inmobiliaria,
          })
          return null
        }
        console.error(error)
      }
    },
    SEARCH_PROPERTIES: async ({ commit, state }, {filters, page, pageSize}) => {
      try {
        commit('SET_FILTERS', filters);
        const { queryString } = buildFilters(filters);
        
        const apiUrl = `${process.env.VUE_APP_API_BASE_URL}/api/company/${window.location.host}/publications/?${queryString}&page_size=${pageSize}&page=${page}`;
        
        console.log('API URL being called:', apiUrl);
        console.log('Query string:', queryString);
        console.log('Filters passed to buildFilters:', filters);

        const response = await axios.get(apiUrl);
        const propertiesResponse = response.data;
        
        console.log('API response:', propertiesResponse);
        
        commit('SET_PROPERTIES', propertiesResponse);
        return propertiesResponse;
      } catch (error) {
        console.error('Error in SEARCH_PROPERTIES action:', error);
        throw error;
      }
    },
    SEARCH_LOCATIONS: async ({ commit }, query) => {
      try {
        const response = await axios.get(
          `${process.env.VUE_APP_API_BASE_URL}/api/localidades/?query=${query}`
        );
        const locations = response.data;
        commit('SET_LOCATIONS', locations);
        return locations;
      } catch (error) {
        console.error(error);
        commit('SET_LOCATIONS', []);
        return [];
      }
    },
    SEARCH_PROPERTY: async ({ commit }, id) => {
      try {
        const response = await axios.get(
          `${process.env.VUE_APP_API_BASE_URL}/api/company/${window.location.host}/publication/${id}`
        );
        return response.data;
      } catch (error) {
        console.error(error);
        return null;
      }
    },
    FETCH_HOME_PUBLICATIONS: async ({ commit }) => {
      try {
        const response = await axios.get(
          `${process.env.VUE_APP_API_BASE_URL}/api/company/${window.location.host}/home/publications/`,
          { withCredentials: true }
        );
        const properties = response.data;
        return commit('SET_PROPERTIES_HOME_PROPERTIES', properties);
      } catch (error) {
        console.error(error);
      }
    },
    FETCH_BLOG_POSTS: async ({ commit }) => {
      try {
        const response = await axios.get(
          `${process.env.VUE_APP_API_BASE_URL}/api/company/${window.location.host}/blog/`
        );
        const posts = response.data;
        commit('SET_BLOG_POSTS', posts);
      } catch (error) {
        console.error(error);
        return [];
      }
    },
  },
  modules: {
    auth,
    favorites,
    appointments,
    comparison,
    rent,
    billing,
    savedSearches,
  },
})
